/**
 * Activity Invariants (Actividad tab)
 *
 * Enforces:
 * 1. Permissions — source-based command access control (permission matrix).
 * 2. Normalization — light state consistency (page clamping).
 * 3. Validation — none (no form in this view).
 *
 * Plus exported `selectors` (matching the itemList convention) that derive the
 * flat per-execution views the components and policies consume (TV1-1971).
 */

import { RulesExecutionsReadmodelType } from '@bett3r-dev/teselly-domain';
import { CommandOperationMap, Invariants, gateCommandByOperation } from '@commandDrivenStateEngine/core/invariants';
import { ActivityCommand } from './commands';
import { ActivityState } from './state';

/**
 * Command operations — the trace modal's "Reintentar" click flips
 * `loading/setRetrying` ( false → true ), which the `tracePolicy` reacts to by
 * calling `RetryRuleExecution`. Capability is DERIVED from the named client
 * method. Other `ui/*` interactions are not mutations → ungated.
 */
export const commandOperations: CommandOperationMap<ActivityState, ActivityCommand> = {
  'automation/activity/loading/setRetrying': 'RetryRuleExecution'
};

/** Number of Rule Executions rendered per page (flat Actividad list, TV1-1971). */
export const EXECUTIONS_PAGE_SIZE = 20;

/**
 * Derived state selectors.
 *
 * Heavy lifting (sort / paginate / counts) lives here so `normalize` stays light
 * and components/policies share one source of truth.
 */
export const selectors = {
  /**
   * Flat Rule-Execution list (TV1-1971), newest-first. The server already sorts
   * `startedAt desc`, but re-sort defensively so a debounced-refetch merge or a
   * signal race never surfaces out-of-order rows.
   */
  sortedExecutions: (
    state: ActivityState
  ): RulesExecutionsReadmodelType[] => {
    return [...state.data.executions].sort(( a, b ) =>
      b.startedAt.localeCompare( a.startedAt ));
  },

  /** Current page slice of the flat execution list (1-based `ui.page`). */
  pagedExecutions: (
    state: ActivityState
  ): RulesExecutionsReadmodelType[] => {
    const sorted = selectors.sortedExecutions( state );
    const start = ( state.ui.page - 1 ) * EXECUTIONS_PAGE_SIZE;
    return sorted.slice( start, start + EXECUTIONS_PAGE_SIZE );
  },

  /**
   * Distinct `triggeringEventName`s across the loaded executions window
   * (TV1-1971, slice 3) — the values that populate the coarse trigger-type chip,
   * mirroring how the status chip is sourced. Sorted alphabetically for a stable
   * dropdown. When a trigger-type chip is active the server-filtered window holds
   * only that type, so the active value is folded in so the dropdown never loses
   * the current selection.
   */
  distinctTriggeringEventNames: ( state: ActivityState ): string[] => {
    const names = new Set<string>();
    for ( const execution of state.data.executions ) {
      if ( execution.triggeringEventName ) names.add( execution.triggeringEventName );
    }
    if ( state.ui.triggeringEventNameFilter ) {
      names.add( state.ui.triggeringEventNameFilter );
    }
    return [...names].sort(( a, b ) => a.localeCompare( b ));
  },

  /**
   * Distinct `hierarchyLevel`s across the loaded executions window (TV1-1971,
   * slice 4) — the values that populate the coarse entity-level chip, mirroring
   * how the trigger-type chip is sourced. Sorted alphabetically for a stable
   * dropdown. When a level chip is active the server-filtered window holds only
   * that level, so the active value is folded in so the dropdown never loses the
   * current selection.
   */
  distinctHierarchyLevels: ( state: ActivityState ): string[] => {
    const levels = new Set<string>();
    for ( const execution of state.data.executions ) {
      if ( execution.hierarchyLevel ) levels.add( execution.hierarchyLevel );
    }
    if ( state.ui.hierarchyLevelFilter ) {
      levels.add( state.ui.hierarchyLevelFilter );
    }
    return [...levels].sort(( a, b ) => a.localeCompare( b ));
  },

  /**
   * The SINGLE running-executions-count source (TV1-1971, slice 2). Reads the
   * FILTER-INDEPENDENT `data.statusCounts.running` (loaded from the dedicated
   * `RuleExecutionsStatusCounts` backend query), NOT the loaded (server-filtered)
   * `data.executions` window — so it stays correct even when the list chip is on
   * `completed`/`failed` (which server-filters out every running row). The
   * LiveStrip, the page-header pill, and the status chip's "running" label ALL
   * read this one selector so the running count is consistent across the screen.
   */
  runningExecutionsCount: ( state: ActivityState ): number => {
    return state.data.statusCounts.running;
  },

  /**
   * Per-chip bucket counts (all / running / failed / completed), read straight
   * from the FILTER-INDEPENDENT `data.statusCounts` — the dedicated counts query
   * always reports the full per-status picture for the account, so the badges are
   * correct regardless of the active `ui.executionFilter`. `running` folds
   * `pending` + `in-progress` server-side.
   */
  executionStatusCounts: (
    state: ActivityState
  ): { all: number; running: number; failed: number; completed: number } => {
    return state.data.statusCounts;
  }
};

/**
 * Normalize state for consistency. Kept intentionally light — the derived
 * execution views live in the exported selectors.
 *
 * - Clamps `ui.page` to a valid range based on the loaded execution count.
 */
const normalize = ( state: ActivityState ): ActivityState => {
  const executionCount = state.data.executions.length;
  const totalPages = Math.max( Math.ceil( executionCount / EXECUTIONS_PAGE_SIZE ), 1 );

  let page = state.ui.page;
  if ( page > totalPages ) page = totalPages;
  if ( page < 1 ) page = 1;

  if ( page === state.ui.page ) return state;

  return { ...state, ui: { ...state.ui, page }};
};

/**
 * Permission control — source-based command access.
 *
 * Per the permission matrix:
 * - `user` drives all `ui/*` interactions (selection, filters, open/close, pin).
 * - `api` (policies) drives all `data/*` and `loading/*` commands — these carry
 *   subscription/query results, the retry submission trigger, and the
 *   read-after-write refetch (memory `feedback_cdse_submission_trigger_canapply`).
 * - `user` ALSO drives ONE `loading/*` command — `loading/setRetrying`. The
 *   trace modal's "Reintentar" click flips `loading.retrying[id]` to `true` as
 *   the submission trigger the trace policy reacts to. Per the CDSE
 *   submission-trigger pattern (memory `feedback_cdse_submission_trigger_canapply`),
 *   this user-sourced loading command MUST be explicitly allowlisted or the
 *   click is silently dropped by canApply. No other `loading/*` command is
 *   broadened to `user` — they stay api-only.
 * - `application`/`policy` get full access for internal coordination.
 */
const canApply = ({
  state,
  command,
  meta
}: {
  state: ActivityState;
  command: ActivityCommand;
  meta: import( '@commandDrivenStateEngine/core/types' ).CommandMeta;
}): boolean => {
  const { source } = meta;
  const { type } = command;

  // API source: policies execute data/* and loading/* commands.
  if ( source === 'api' ) {
    return (
      type.startsWith( 'automation/activity/data/' ) ||
      type.startsWith( 'automation/activity/loading/' )
    );
  }

  // User source: all UI interactions, plus the ONE allowlisted retry
  // submission trigger. `loading/setRetrying` from `user` is the trace modal's
  // "Reintentar" click that the trace policy reacts to (false → true). Without
  // this explicit exception the click is silently dropped (memory
  // `feedback_cdse_submission_trigger_canapply`). All other `loading/*` commands
  // remain api-only.
  if ( source === 'user' ) {
    if ( type.startsWith( 'automation/activity/ui/' )) return true;
    if ( type === 'automation/activity/loading/setRetrying' ) {
      return gateCommandByOperation( commandOperations, { state, command, meta });
    }
    return false;
  }

  // Policy / application source: full access for internal coordination.
  if ( source === 'policy' || source === 'application' ) {
    return true;
  }

  // Unknown sources (incl. ai, url): deny.
  return false;
};

/** No form validation needed for the activity view. */
const validate = ( _state: ActivityState ): Record<string, string> | null => {
  return null;
};

export const invariants: Invariants<ActivityState, ActivityCommand> = {
  normalize,
  canApply,
  validate
};
